import { useEffect, useRef } from 'react';
import { useTour } from '@reactour/tour';
import { getActiveTourKey } from './activeTour';
import { CANPARK_TICKET_STEP, CANPARK_STAGE_RANGE } from './tourRegistry';

const TOUR_KEY = 'canpark_issue_ticket';

// Drives the CANpark ticketing tour from the app's real state instead of Next
// buttons. Call from a component mounted inside ActiveVehicleProvider on
// /canpark, passing the live drawer state.
//
// The in-drawer pipeline is data-driven per lot: stages the site didn't enable
// simply never become the active `stage`, so their tour steps are never
// targeted — that's the conditional rendering, for free. We map the live stage
// to its step; on drawer close we finish (if the user reached the ticket form)
// or reset to the scan step (if they bailed earlier). Welcome/Finish are
// centered slides the user controls, so we don't override those.
export const useTicketingTourSync = (
	showDrawer: boolean,
	stage: string,
	pipeline: { id: string }[],
	captureMode: 'none' | 'camera' | 'manual' | 'preview',
	uppyOpen: boolean,
) => {
	const { isOpen, currentStep, setCurrentStep } = useTour();
	const prevShowDrawerRef = useRef(showDrawer);

	useEffect(() => {
		const justClosed = prevShowDrawerRef.current && !showDrawer;
		prevShowDrawerRef.current = showDrawer;

		if (!isOpen || getActiveTourKey() !== TOUR_KEY) return;
		if (
			currentStep === CANPARK_TICKET_STEP.WELCOME ||
			currentStep === CANPARK_TICKET_STEP.FINISH
		) {
			return;
		}

		let target: number | null = null;
		if (showDrawer && uppyOpen) {
			// Uppy upload modal (Take Photo / Attach) is open — step aside to the
			// click-through top popover so it doesn't cover the Upload/Done buttons.
			target = CANPARK_TICKET_STEP.UPLOADING;
		} else if (showDrawer) {
			// Follow the live drawer stage — it's authoritative (the drawer renders
			// by `stage`). Re-sync only when we're outside this stage's step range,
			// so paging through a stage's sub-steps (e.g. ticket overview -> Save)
			// sticks. A brief mismatch (default 'action' before the provider
			// corrects it) just hides the popover, since that step's anchor isn't
			// mounted yet — no visible flash.
			const range = CANPARK_STAGE_RANGE[stage];
			if (range && (currentStep < range[0] || currentStep > range[1])) {
				target = range[0];
			}
		} else if (captureMode === 'manual') {
			// Manual "Enter license plate" sheet — popover points at the sheet.
			target = CANPARK_TICKET_STEP.MANUAL_ENTRY;
		} else if (captureMode === 'preview') {
			// Post-scan review — prompt to "Confirm & continue".
			target = CANPARK_TICKET_STEP.PREVIEW;
		} else if (captureMode === 'camera') {
			// Camera viewfinder — prompt to capture.
			target = CANPARK_TICKET_STEP.CAMERA;
		} else if (justClosed && currentStep >= CANPARK_TICKET_STEP.TICKET_INTRO) {
			// Closed after reaching the form (submitted, or finished the tow branch).
			target = CANPARK_TICKET_STEP.FINISH;
		} else {
			// Not in a ticket yet (or bailed before the form) — sit on the scan step.
			target = CANPARK_TICKET_STEP.SCAN;
		}

		if (target != null && target !== currentStep) setCurrentStep(target);
	}, [
		isOpen,
		currentStep,
		showDrawer,
		stage,
		pipeline,
		captureMode,
		uppyOpen,
		setCurrentStep,
	]);
};
